import React, { useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'

import { LIBRARY_TABS } from '../../constant'
import { mapLibraryItem } from '../../mapLibraryItem'
import { useLibrarySelection } from '../../hooks/useLibrarySelection'
import PortalModal from 'components/Shared/PortalModal'
import RenameModal from '../Modals/RenameModal'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import ConfirmationModal from 'components/Shared/ConfirmationModal'
import SelectedPopup from '../Modals/SelectedPopup'
import { PreviewLibraryModal } from '../Modals/PreviewModal'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import LibraryGrid from './LibraryGrid'

export default function LibraryList({
  selectiveMode,
  setSelectiveMode,
  isUploading = false,
  activeTab,
  data,
  isLoading,
  isFetchingMore,
  hasMore,
  isError,
  isFetchMoreError,
  hasFilters,
  fetchNextPage,
  retryFetchMore,
  renameImageById,
  deleteImageById,
  deleteImagesByGroups,
  onLibraryMutated,
}) {
  const [imageToRename, setImageToRename] = useState(null)
  const [imageToDelete, setImageToDelete] = useState(null)
  const [imageToPreview, setImageToPreview] = useState(null)
  const [deleteFromPreview, setDeleteFromPreview] = useState(false)
  const [previewDeletedImageId, setPreviewDeletedImageId] = useState(null)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isDeletingImage, setIsDeletingImage] = useState(false)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false)
  const [isDeletingSelectedImages, setIsDeletingSelectedImages] = useState(false)

  const { t } = useTranslation()
  const { addNotification } = useToast()

  const { selectedItems, selectedIds, createSetSelectedMedia, deselectAll } = useLibrarySelection()

  const mappedItems = useMemo(
    () => (data?.items ?? []).map((apiItem) => mapLibraryItem(apiItem)),
    [data?.items],
  )

  const setSelectedMedia = useMemo(
    () => createSetSelectedMedia(mappedItems),
    [createSetSelectedMedia, mappedItems],
  )

  useEffect(() => {
    if (!selectiveMode) {
      deselectAll()
    }
  }, [selectiveMode, deselectAll])

  const showSelectionPopup = selectiveMode && selectedItems.length > 0

  const handleRename = async (nextName) => {
    if (!imageToRename) {
      return
    }

    setIsRenaming(true)

    const response = await renameImageById({
      imageId: imageToRename.id,
      imageName: nextName,
      groupId: imageToRename.group_id,
    })

    setIsRenaming(false)

    if (response.success) {
      addNotification({
        message: t('Library.renameModal.toastSuccess.title'),
        description: t('Library.renameModal.toastSuccess.description'),
        delay: 3000,
      })
      setImageToRename(null)
      return
    }

    addNotification({
      message: t('Library.renameModal.toastError.title'),
      description: t('Library.renameModal.toastError.description'),
      variant: 'error',
      delay: 3000,
    })
  }

  const handleDeleteImage = async () => {
    if (!imageToDelete) {
      return
    }

    setIsDeletingImage(true)

    const response = await deleteImageById({
      imageId: imageToDelete.id,
      groupId: imageToDelete.group_id,
      isOriginal: imageToDelete.original,
    })

    setIsDeletingImage(false)

    if (response.success) {
      addNotification({
        message: t('Library.deleteModal.toastSuccess.title'),
        description: t('Library.deleteModal.toastSuccess.description'),
        delay: 3000,
      })

      if (deleteFromPreview) {
        if (imageToDelete.id === imageToPreview?.id) {
          setImageToPreview(null)
        } else {
          setPreviewDeletedImageId(imageToDelete.id)
        }
        setDeleteFromPreview(false)
      }

      setImageToDelete(null)
      onLibraryMutated?.()
      return
    }

    addNotification({
      message: t('Library.deleteModal.toastError.title'),
      description: t('Library.deleteModal.toastError.description'),
      variant: 'error',
      delay: 3000,
    })
  }

  const handleCancelDelete = () => {
    setImageToDelete(null)
    setDeleteFromPreview(false)
  }

  const handleDeleteSelectedImages = async () => {
    if (!selectedItems.length) {
      return
    }

    const payload = selectedItems.reduce((acc, { item }) => {
      if (!item?.group_id) {
        return acc
      }

      const groupId = String(item.group_id)
      if (!acc[groupId]) {
        acc[groupId] = []
      }
      acc[groupId].push(item.id)
      return acc
    }, {})

    if (!Object.keys(payload).length) {
      addNotification({
        message: t('Library.deleteModal.toastError.title'),
        description: t('Library.deleteModal.toastError.description'),
        variant: 'error',
        delay: 3000,
      })
      return
    }

    const removedVariantCountByGroup = selectedItems.reduce((acc, { item }) => {
      if (!item?.group_id || item.original) {
        return acc
      }

      acc.set(item.group_id, (acc.get(item.group_id) ?? 0) + 1)
      return acc
    }, new Map())

    setIsDeletingSelectedImages(true)

    const response = await deleteImagesByGroups(payload, removedVariantCountByGroup)

    setIsDeletingSelectedImages(false)

    if (response.success) {
      const deletedImageIds = new Set(selectedIds)

      addNotification({
        message: t('Library.deleteModal.toastSuccess.title'),
        description: t('Library.deleteModal.toastSuccess.description'),
        delay: 3000,
      })

      if (deletedImageIds.has(imageToPreview?.id)) {
        setImageToPreview(null)
      }

      setIsBulkDeleteModalOpen(false)
      deselectAll()
      setSelectiveMode(false)
      onLibraryMutated?.()
      return
    }

    addNotification({
      message: t('Library.deleteModal.toastError.title'),
      description: t('Library.deleteModal.toastError.description'),
      variant: 'error',
      delay: 3000,
    })
  }

  const handleExitSelectionMode = () => {
    deselectAll()
    setSelectiveMode(false)
  }

  const getActionMenuItems = (item) => [
    {
      key: 'preview',
      label: t('Recommendations.campaign.setup.media.dropdown.preview'),
      onClick: () => setImageToPreview(item),
    },
    {
      key: 'rename',
      label: t('Library.actions.rename', { defaultValue: 'Rename' }),
      onClick: () => setImageToRename(item),
    },
    {
      key: 'delete',
      label: t('Library.actions.delete', { defaultValue: 'Delete' }),
      onClick: () => setImageToDelete(item),
    },
  ]

  return (
    <div className={classnames('library-list')}>
      {isUploading && (
        <div className="library-list__upload-overlay" aria-busy="true">
          <LoadingSpinner text={t('common.loading')} portalBackground={false} />
        </div>
      )}

      <LibraryGrid
        mappedItems={mappedItems}
        isLoading={isLoading}
        isError={isError}
        isFetchingMore={isFetchingMore}
        hasMore={hasMore}
        isFetchMoreError={isFetchMoreError}
        hasFilters={hasFilters}
        fetchNextPage={fetchNextPage}
        retryFetchMore={retryFetchMore}
        selectiveMode={selectiveMode}
        selectedMedia={selectedIds}
        setSelectedMedia={setSelectedMedia}
        enableVariantClick={activeTab !== LIBRARY_TABS.ORIGINAL}
        onPreviewClick={setImageToPreview}
        getActionMenuItems={getActionMenuItems}
      />

      <PortalModal
        isOpen={!!imageToRename}
        onClose={() => setImageToRename(null)}
        title={t('Library.renameModal.title')}
        showCloseButton={false}
        className="rename-modal"
      >
        <RenameModal
          image={imageToRename}
          onClose={() => setImageToRename(null)}
          onSave={handleRename}
          isLoading={isRenaming}
        />
      </PortalModal>

      <PortalModal isOpen={!!imageToDelete} onClickOutside={handleCancelDelete}>
        <ConfirmationModal
          title={t('Library.deleteModal.title')}
          description={t('Library.deleteModal.description')}
          onCancel={handleCancelDelete}
          onConfirm={handleDeleteImage}
          confirmText={t('Library.deleteModal.btnDelete')}
          isLoading={isDeletingImage}
        />
      </PortalModal>

      <PortalModal isOpen={isBulkDeleteModalOpen} onClickOutside={() => setIsBulkDeleteModalOpen(false)}>
        <ConfirmationModal
          title={t('Library.deleteModal.titlePlural')}
          description={t('Library.deleteModal.descriptionPlural', { count: selectedItems.length })}
          onCancel={() => setIsBulkDeleteModalOpen(false)}
          onConfirm={handleDeleteSelectedImages}
          confirmText={t('Library.deleteModal.btnDelete')}
          isLoading={isDeletingSelectedImages}
        />
      </PortalModal>

      <PortalModal
        isOpen={!!imageToPreview}
        onClickOutside={() => setImageToPreview(null)}
        title={t('Library.previewModal.title')}
        className="library-preview-modal"
      >
        <PreviewLibraryModal
          file={imageToPreview}
          onClose={() => setImageToPreview(null)}
          onDeleteClick={(image) => {
            setImageToDelete(image)
            setDeleteFromPreview(true)
          }}
          deletedImageId={previewDeletedImageId}
          onDeletedImageHandled={() => setPreviewDeletedImageId(null)}
        />
      </PortalModal>

      {showSelectionPopup && (
        <div className="library-list__selection-bar">
          <SelectedPopup
            count={selectedItems.length}
            onCancel={handleExitSelectionMode}
            onDelete={() => setIsBulkDeleteModalOpen(true)}
          />
        </div>
      )}
    </div>
  )
}
